import { CLASS_NAME } from 'constants/class-names'
import {
    EventType,
    HtmlAttr,
    KeyType,
    SlidingDrawerEvent
} from 'constants/enums'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'

const enum SlidingDrawerAnimations {
    SLIDEDOWN = 'drawerSlideDown',
    SLIDEOUT = 'slideOutToLeft',
    SLIDEUP = 'drawerSlideUp',
    SLIDEIN = 'slideInFromLeft'
}
// SlidingDrawerEvent.CLOSED event is triggered only for SlidingDrawer
// positioned at the bottom or left side of the page.
const SlidingDrawerAnimationsClosed: string[] = [
    SlidingDrawerAnimations.SLIDEDOWN,
    SlidingDrawerAnimations.SLIDEOUT
]
// SlidingDrawerEvent.OPENED event is triggered only for SlidingDrawer
// positioned at the bottom or left side of the page.
const SlidingDrawerAnimationsOpened: string[] = [
    SlidingDrawerAnimations.SLIDEUP,
    SlidingDrawerAnimations.SLIDEIN
]

export class SlidingDrawerComponent extends ComponentBase {
    private drawerClose: Nullable<HTMLButtonElement>

    /** cache the result of the DOM query the first time the getter is accessed and
     * return this cached result on subsequent accesses. This way, the DOM query is
     * performed only once, reducing the workload on the main thread. INP/TBT
     */
    /**
     * Get backdrop element if option provided from Django template
     * @return {Nullable<HTMLElement>}
     */
    private _backdropEl: Nullable<HTMLElement> = undefined
    private get backdropEl(): Nullable<HTMLElement> {
        if (!this._backdropEl) {
            this._backdropEl = this.getElement(
                `.${CLASS_NAME.slidingDrawer.backdropEl}`
            )
        }
        return this._backdropEl
    }

    /**
     * Get drawer element (support for dynamic element)
     * @return {Nullable<HTMLElement>}
     */
    private get drawer(): Nullable<HTMLElement> {
        return this.getElement(`#${this.drawerEl.id}`)
    }

    constructor(private drawerEl: HTMLElement) {
        super()

        if (!(drawerEl.id && this.drawer)) {
            return
        }

        this.drawerClose = this.getElement<HTMLButtonElement>(
            `.${CLASS_NAME.slidingDrawer.close}`,
            this.drawer
        )

        window.addEventListener(SlidingDrawerEvent.OPEN, (ev: CustomEvent) =>
            this.openDrawer(ev.detail)
        )
        window.addEventListener(SlidingDrawerEvent.CLOSE, (ev: CustomEvent) =>
            this.closeDrawer(ev.detail)
        )
        // To hide content from both keyboard and screen reader
        window.addEventListener(SlidingDrawerEvent.CLOSED, () => {
            if (this.drawer) {
                this.drawer.hidden = true
            }
        })
        // watch for completion of animation that opens and closes the drawer
        // dispatch event for their completion
        document.addEventListener(
            EventType.ANIMATION_END,
            (ev: AnimationEvent) => this.animationEndEventsHandler(ev)
        )

        // close drawer on backdrop click (support for dynamic element)
        // close drawer on close button click (support for dynamic element)
        document.body.addEventListener('click', (evt) => {
            if (!(evt.target instanceof Element)) {
                return
            }
            const isBackdropClick = evt.target.classList.contains(
                `${CLASS_NAME.slidingDrawer.backdropEl}`
            )
            const isCloseButtonClick =
                evt.target.classList.contains(
                    `${CLASS_NAME.slidingDrawer.close}`
                ) ||
                evt.target.parentElement?.classList.contains(
                    `${CLASS_NAME.slidingDrawer.close}`
                )

            if (isBackdropClick) {
                if (this.backdropEl && this.backdropEl.dataset) {
                    this.closeDrawer(this.backdropEl.dataset.backdropSibling)
                }
                this.enableScroll()
            } else if (isCloseButtonClick) {
                if (this.drawer) {
                    this.closeDrawer(this.drawer.id)
                }
            }
        })

        if (this.drawerClose) {
            // when dismiss-able is allowed a close button should exist
            // attach escape key exit then
            document.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
                if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
                    if (
                        this.drawer &&
                        this.drawer.classList.contains(
                            CLASS_NAME.slidingDrawer.slideUp
                        )
                    ) {
                        this.closeDrawer(this.drawer.id)
                        this.enableScroll()
                    }
                }
            })
        }
    }

    private openDrawer(drawerId: string): void {
        if (!this.drawer || this.drawer.id !== drawerId) {
            return
        }
        this.drawer.classList.remove(CLASS_NAME.slidingDrawer.slideDown)
        this.drawer.classList.add(CLASS_NAME.slidingDrawer.slideUp)
        this.drawer.removeAttribute(HtmlAttr.TAB_INDEX)
        this.drawer.hidden = false
        this.disableScroll()
    }

    private closeDrawer(drawerId = ''): void {
        if (this.drawer && this.drawer.id !== drawerId) {
            return
        }
        this.closeAll()
        this.enableScroll()
    }

    private closeAll(): void {
        if (!this.drawer) {
            return
        }
        this.drawer.classList.remove(CLASS_NAME.slidingDrawer.slideUp)
        this.drawer.classList.add(CLASS_NAME.slidingDrawer.slideDown)
        this.drawer.tabIndex = -1
    }

    private enableScroll(): void {
        if (this.drawer && this.drawer.dataset.backdrop && this.backdropEl) {
            document.body.classList.remove(
                CLASS_NAME.slidingDrawer.backdropSupport
            )
        }
    }

    private disableScroll(): void {
        if (this.drawer && this.drawer.dataset.backdrop && this.backdropEl) {
            document.body.classList.add(
                CLASS_NAME.slidingDrawer.backdropSupport
            )
        }
    }

    private animationEndEventsHandler(ev: AnimationEvent): void {
        if (SlidingDrawerAnimationsClosed.includes(ev.animationName)) {
            window.dispatchEvent(new CustomEvent(SlidingDrawerEvent.CLOSED))
        } else if (SlidingDrawerAnimationsOpened.includes(ev.animationName)) {
            window.dispatchEvent(new CustomEvent(SlidingDrawerEvent.OPENED))
        }
    }
}
